<template>
  <div class="post-page">

    <!-- Loading -->
    <div v-if="!post.title" class="post-page__loading">
      <spinner class="text-primary"></spinner>
    </div>

    <template v-else>
      <!-- Hero -->
      <div class="post-hero" :style="post.feature_image ? `background-image:url(${post.feature_image})` : ''">
        <div class="post-hero__overlay"></div>
        <div class="post-hero__content container">
          <router-link to="/insights" class="post-hero__back">
            <i class="fa fa-arrow-left"></i> Back to Insights
          </router-link>
          <span class="insights-badge" :class="'insights-badge--' + post.post_type">
            {{ typeLabel(post.post_type) }}
          </span>
          <h1 class="post-hero__title">{{ post.title }}</h1>
          <div class="post-hero__meta">
            <span v-if="post.author"><i class="fa fa-user-circle"></i> {{ post.author }}</span>
            <span v-if="post.author" class="post-meta-dot">·</span>
            <span><i class="fa fa-calendar"></i> {{ formatDate(post.created_at) }}</span>
            <span v-if="post.read_time" class="post-meta-dot">·</span>
            <span v-if="post.read_time"><i class="fa fa-clock-o"></i> {{ post.read_time }} read</span>
          </div>
        </div>
      </div>

      <!-- Article body -->
      <div class="post-body">
        <div class="container">
          <div class="post-body__inner">
            <!-- Excerpt -->
            <p v-if="post.excerpt" class="post-body__excerpt">{{ post.excerpt }}</p>

            <!-- Content -->
            <div class="post-body__content" v-html="post.content"></div>

            <!-- Share -->
            <div class="post-body__share">
              <span class="post-body__share-label">Share this insight</span>
              <share-facebook></share-facebook>
              <share-twitter></share-twitter>
              <share-linkedin></share-linkedin>
            </div>
          </div>
        </div>
      </div>

      <!-- Similar posts -->
      <div v-if="similar_posts && similar_posts.length" class="post-similar">
        <div class="container">
          <h2 class="post-similar__title">You may also like</h2>
          <div class="post-similar__grid">
            <div
              v-for="p in similar_posts"
              :key="p.id"
              class="post-similar__card"
              @click="$router.push('/insights/' + p.id + '/' + p.slug)"
            >
              <div class="post-similar__img-wrap">
                <img v-if="p.feature_image" :src="p.feature_image" :alt="p.title" @error="e => e.target.style.display='none'" />
                <div class="post-similar__img-placeholder"></div>
              </div>
              <div class="post-similar__body">
                <span class="insights-badge" :class="'insights-badge--' + p.post_type">{{ typeLabel(p.post_type) }}</span>
                <h3 class="post-similar__card-title">{{ p.title }}</h3>
                <div class="post-similar__meta">
                  <span>{{ formatDate(p.created_at) }}</span>
                  <span v-if="p.read_time" class="post-meta-dot">·</span>
                  <span v-if="p.read_time">{{ p.read_time }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Moment from "moment";
import { insightPreview, isPreview } from "@/services/cms";
const Spinner = () => import("@/components/Spinner");
import ShareFacebook from "@/components/ShareButtons/Facebook";
import ShareTwitter from "@/components/ShareButtons/Twitter";
import ShareLinkedin from "@/components/ShareButtons/Linkedin";

export default {
  components: { Spinner, ShareFacebook, ShareTwitter, ShareLinkedin },
  data() {
    return {
      post: { title: null, content: null },
      similar_posts: null,
      typeLabels: { article: "Article", research: "Research", case_study: "Case Study", briefing: "Briefing" }
    };
  },
  metaInfo() {
    return {
      title: this.post.title,
      meta: [{ name: "description", content: this.post.excerpt || "" }]
    };
  },
  mounted() {
    this.getBlogPostDetail();
  },
  methods: {
    async getBlogPostDetail() {
      // Preview mode: fetch draft data from the insight preview endpoint
      if (isPreview()) {
        const token = new URLSearchParams(window.location.search).get('token');
        const data = await insightPreview(token);
        if (data) { this.post = data; return; }
      }
      this.$axios
        .get("insights/post/" + this.$route.params.id + "/")
        .then(res => {
          if (this.$route.params.slug === res.data.slug) {
            this.post = res.data;
            this.getOtherBlogPosts();
          } else {
            this.$router.push("/404");
          }
        })
        .catch(err => { if (err.response) this.$router.push("/404"); });
    },
    getOtherBlogPosts() {
      this.$axios
        .post("insights/similar-post/", { id: this.$route.params.id })
        .then(res => { this.similar_posts = res.data; });
    },
    formatDate(date) { return Moment(date).format("MMM D, YYYY"); },
    typeLabel(type) { return this.typeLabels[type] || type; }
  },
  watch: {
    $route() {
      this.post = { title: null, content: null };
      this.similar_posts = null;
      this.getBlogPostDetail();
    }
  }
};
</script>

<style scoped>
.post-page__loading { min-height: 60vh; display: flex; align-items: center; justify-content: center; }

/* Hero */
.post-hero {
  position: relative;
  min-height: 480px;
  background: #0f172a center/cover no-repeat;
  display: flex;
  align-items: flex-end;
  padding-bottom: 56px;
}
.post-hero__overlay {
  position: absolute; inset: 0;
  background: linear-gradient(to bottom, rgba(15,23,42,0.35) 0%, rgba(15,23,42,0.82) 100%);
}
.post-hero__content {
  position: relative;
  z-index: 1;
  max-width: 780px;
}
.post-hero__back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(255,255,255,0.75);
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  margin-bottom: 20px;
  transition: color 0.15s;
}
.post-hero__back:hover { color: #fff; text-decoration: none; }
.post-hero__title {
  font-size: clamp(1.6rem, 4vw, 2.75rem);
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
  margin: 12px 0 20px;
}
.post-hero__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: rgba(255,255,255,0.75);
}
.post-hero__meta i { margin-right: 4px; }
.post-meta-dot { color: rgba(255,255,255,0.4); }

/* Badges (reuse from insights page, scoped so redeclare) */
.insights-badge {
  display: inline-block; padding: 3px 12px; border-radius: 100px;
  font-size: 11px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
}
.insights-badge--article    { background: #dbeafe; color: #1d4ed8; }
.insights-badge--research   { background: #dcfce7; color: #15803d; }
.insights-badge--case_study { background: #fef9c3; color: #a16207; }
.insights-badge--briefing   { background: #fce7f3; color: #be185d; }

/* Body */
.post-body { background: #f8fafc; padding: 56px 0 72px; }
.post-body__inner {
  max-width: 720px;
  margin: 0 auto;
  background: #fff;
  border-radius: 16px;
  padding: 48px 56px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.06);
}
.post-body__excerpt {
  font-size: 1.15rem;
  color: #475569;
  line-height: 1.7;
  font-style: italic;
  border-left: 3px solid #0f172a;
  padding-left: 20px;
  margin-bottom: 32px;
}
.post-body__content {
  font-size: 1.05rem;
  line-height: 1.85;
  color: #1e293b;
}
/* Typography inside rich text */
.post-body__content >>> h1,
.post-body__content >>> h2,
.post-body__content >>> h3 { font-weight: 700; color: #0f172a; margin: 32px 0 16px; line-height: 1.3; }
.post-body__content >>> h2 { font-size: 1.5rem; }
.post-body__content >>> h3 { font-size: 1.2rem; }
.post-body__content >>> p  { margin-bottom: 20px; }
.post-body__content >>> a  { color: #2563eb; text-decoration: underline; }
.post-body__content >>> ul,
.post-body__content >>> ol { padding-left: 24px; margin-bottom: 20px; }
.post-body__content >>> li { margin-bottom: 8px; }
.post-body__content >>> blockquote {
  border-left: 3px solid #e2e8f0; padding-left: 20px;
  color: #64748b; font-style: italic; margin: 24px 0;
}
.post-body__content >>> img { max-width: 100%; border-radius: 8px; margin: 16px 0; }

/* Share */
.post-body__share {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}
.post-body__share-label { font-size: 13px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 1px; margin-right: 4px; }

/* Similar */
.post-similar { background: #fff; padding: 64px 0 80px; }
.post-similar__title { font-size: 1.5rem; font-weight: 700; color: #0f172a; margin-bottom: 32px; }
.post-similar__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
.post-similar__card {
  border-radius: 16px;
  overflow: visible;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-shadow: none;
}
.post-similar__card:hover { transform: none; border-color: rgba(87,94,207,0.4); box-shadow: 0 2px 16px rgba(87,94,207,0.07); }
.post-similar__card:hover .post-similar__card-title { color: #575ecf; }
.post-similar__img-wrap { display: none !important; }
.post-similar__body {
  padding: 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 160px;
}
.post-similar__body::before {
  content: '\f0f6';
  font-family: FontAwesome;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(87,94,207,0.1);
  color: #575ecf;
  font-size: 16px;
  margin-bottom: 16px;
  flex-shrink: 0;
}
.post-similar__card .insights-badge {
  position: absolute;
  top: 24px;
  right: 24px;
  background: none !important;
  color: #94a3b8 !important;
  border-radius: 0;
  padding: 0;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
}
.post-similar__card-title { font-size: 0.95rem; font-weight: 600; letter-spacing: -0.01em; color: #0a1428; line-height: 1.45; margin: 0 0 auto; flex: 1; transition: color 0.15s; }
.post-similar__meta { font-size: 11px; color: #94a3b8; display: flex; gap: 6px; align-items: center; border-top: 1px solid #e2e8f0; padding-top: 12px; margin-top: 16px; }

@media (max-width: 768px) {
  .post-body__inner { padding: 28px 20px; }
  .post-similar__grid { grid-template-columns: 1fr; }
  .post-hero { min-height: 360px; }
}
</style>
